import { selectUserInfo } from "features/user/userSlice";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import NetSales from "components/Analytics/NetSales";
import { brandingInfo } from "features/server/brandingSlice";
import { BrandingResponse } from "subModule/src/services/allcommon/interface";
import { themes } from "theme/themes";
import { useEffect, useMemo, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import { useSnackbar } from "notistack";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import TargetvsSales from "components/Analytics/TargetvsSales";
import WorkInProgress from "components/Analytics/WorkInProgress";
import CompletedWorkOrders from "components/Analytics/CompletedWorkOrders";
import RevenueCostProfit from "components/Analytics/RevenueCostProfit";
import TopSales from "components/Analytics/TopSales";
import NetSalesTechnicians from "components/Analytics/NetSalesTechnicians";
import AverageTicketPrice from "components/Analytics/AverageTicketPrice";
import AnalyticsHighlightCard from "components/Analytics/AnalyticsHighlightCard";
import PaymentsOverview from "components/Analytics/PaymentsOverview";
import ExpensesWeekly from "components/Analytics/ExpensesWeekly";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ExpensesByType from "components/Analytics/ExpensesByType";
import ServiceCancellationReasons from "components/Analytics/ServiceCancellationReasons";
import BayInformation from "components/Analytics/BayInformation";
import JobCardOpenVsClosed from "components/Analytics/JobCardOpenVsClosed";
import CustomerBreakdown from "components/Analytics/CustomerBreakdown";
import TopServicesPerformed from "components/Analytics/TopServicesPerformed";
import TopMakeModelServices from "components/Analytics/TopMakeModelServices";
import AverageTimePerJC from "components/Analytics/AverageTimePerJC";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import DashboardPageTitle from "components/Analytics/DashboardPageTitle";
import { DASHBOARD_CHART_WIDGET_HEIGHT } from "components/Analytics/dashboardWidgetLayout";
export default function Analytics() {
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const largeScreen = useMediaQuery("(min-width:1200px)");
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const [stationsLoaded, setStationsLoaded] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [globalFilterApplied, setGlobalFilterApplied] =
    useState<boolean>(false);
  const { control, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      globalFilters: {
        range: { value: "monthly", label: t("mtd") },
        allStation: false,
      },
    },
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const firstRender = useRef(true);
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: pageNo,
      size: 50,
      name: dropdownSearchTerm,
    });
  const themeColors =
    (branding as BrandingResponse)?.attributes?.themeId === 100
      ? (branding as BrandingResponse)?.attributes?.customTheme
      : (themes as any)[
          (branding as BrandingResponse)?.attributes?.themeId || 3
        ];
  useEffect(() => {
    const getStationsData = setTimeout(async () => {
      await stationsRefetch();
      setStationsLoaded(true);
    }, 50);
    return () => clearTimeout(getStationsData);
  }, []);
  const allStation = 0;
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number | null; name: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  if ((stationsData?.pages[0]?.data?.content?.length ?? 0) > 1)
    stations.splice(0, 0, { value: allStation, label: t("allStations") });
  const durationOptions = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  useEffect(() => {
    if (firstRender.current && stationsLoaded && stations.length > 0) {
      firstRender.current = false;
      const defaultStation =
        (stations.length > 1 ? stations[1] : stations[0]) || null;
      setValue("globalFilters.stationId", defaultStation);
    }
  }, [stationsLoaded, stations]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => stationsRefetch().finally(() => setSearchLoading(null)),
      300,
    );
  };
  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        className="mb-[-8px] sticky top-[43px] z-10"
        style={{ background: themeColors?.appBg || "#F1F6F9" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} lg={5}>
            <DashboardPageTitle
              title={t("operationsOverview")}
              color={themeColors?.primary}
            />
          </Grid>
          <Grid item xs={12} md={8} lg={7}>
            <Grid container spacing={2} className="justify-end pt-2">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.stationId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={stations}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        if (selectedOption?.value === 0)
                          setValue("globalFilters.allStation", true);
                        else setValue("globalFilters.allStation", false);
                        setGlobalFilterApplied(true);
                      }}
                      isLoading={searchLoading === "globalFilters"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "globalFilters",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleDropdownActions(
                              "globalFilters",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(stationsData?.pages[0]?.data as any).last)
                          handleDropdownActions("globalFilters", "loadNext");
                      }}
                      page={pageNo}
                      defaultPlaceholder={t("stations")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.range"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={durationOptions}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setGlobalFilterApplied(true);
                      }}
                      defaultPlaceholder={t("duration")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        lg={4}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        {checkPrivileges("ROLE_ANALYTICS_NET_SALES") ? (
          <NetSales
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Box sx={{ opacity: 0.6 }}>
              <AnalyticsHighlightCard
                title={t("netSales")}
                loading
                icon={<PaymentsOutlinedIcon />}
                iconBg={`${themeColors?.primary || "#1CACDC"}18`}
                iconColor={themeColors?.primary || "#1CACDC"}
                value={null}
              />
            </Box>
          </Tooltip>
        )}
        {checkPrivileges("ROLE_ANALYTICS_REVENUE_COST_PROFIT") ? (
          <RevenueCostProfit
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: DASHBOARD_CHART_WIDGET_HEIGHT,
                marginTop: "16px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Skeleton
                  variant="circular"
                  animation={false}
                  width={180}
                  height={180}
                  sx={{
                    backgroundColor: themeColors?.secondary,
                    margin: "10px auto 0 auto",
                  }}
                />
                <Box className={`flex gap-1 justify-between mt-5 items-center`}>
                  <Typography
                    variant="h4"
                    className="font-Saira text-start text-[14px]"
                  >
                    {t("revenue")}
                  </Typography>
                  <Skeleton
                    animation={false}
                    width={180}
                    sx={{ backgroundColor: themeColors?.secondary }}
                  />
                </Box>
                <Box className={`flex gap-1 justify-between items-center`}>
                  <Typography
                    variant="h4"
                    className="font-Saira text-start text-[14px]"
                  >
                    {t("directCost")}
                  </Typography>
                  <Skeleton
                    animation={false}
                    width={180}
                    sx={{ backgroundColor: themeColors?.secondary }}
                  />
                </Box>
                <Box className={`flex gap-1 justify-between items-center`}>
                  <Typography
                    variant="h4"
                    className="font-Saira text-start text-[14px]"
                  >
                    {t("grossProfit")}
                  </Typography>
                  <Skeleton
                    animation={false}
                    width={180}
                    sx={{ backgroundColor: themeColors?.secondary }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        lg={4}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        {checkPrivileges("ROLE_ANALYTICS_COMPLETED_JOBS") ? (
          <CompletedWorkOrders
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Box sx={{ opacity: 0.6 }}>
              <AnalyticsHighlightCard
                title={t("completedWorkOrders")}
                loading
                icon={<AssignmentTurnedInOutlinedIcon />}
                iconBg="#22C55E18"
                iconColor="#22C55E"
                value={null}
              />
            </Box>
          </Tooltip>
        )}
        {checkPrivileges("ROLE_ANALYTICS_STATION_EXPENSES_WEEKLY") ? (
          <ExpensesWeekly
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: DASHBOARD_CHART_WIDGET_HEIGHT,
                marginTop: "16px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-1`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("weeklyExpenses")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{
                    backgroundColor: themeColors?.secondary,
                    marginTop: "10px",
                  }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        lg={4}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        {checkPrivileges("ROLE_ANALYTICS_AVG_TICKET_PRICE") ? (
          <AverageTicketPrice
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Box sx={{ opacity: 0.6 }}>
              <AnalyticsHighlightCard
                title={t("averageTicketPrice")}
                loading
                icon={<ReceiptLongOutlinedIcon />}
                iconBg="#FFAB0018"
                iconColor="#B76E00"
                value={null}
              />
            </Box>
          </Tooltip>
        )}
        {checkPrivileges("ROLE_ANALYTICS_STATION_EXPENSES_BY_TYPE") ? (
          <ExpensesByType
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                height: DASHBOARD_CHART_WIDGET_HEIGHT,
                marginTop: "16px",
                boxShadow: "none",
                borderRadius: "8px",
                opacity: 0.6,
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className="flex gap-1 justify-between mb-1">
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("expensesByCategory")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{
                    backgroundColor: themeColors?.secondary,
                    marginTop: "10px",
                  }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={8}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            {checkPrivileges("ROLE_ANALYTICS_TARGET_VS_SALES") ? (
              <TargetvsSales
                user={loggedInUserData}
                themes={themeColors}
                stations={stations as { id: number; name: string }[] | []}
                handleDropdownActions={handleDropdownActions}
                globalFilters={watch("globalFilters")}
                {...{
                  enqueueSnackbar,
                  stationsLoaded,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  pageNo,
                  searchLoading,
                  setPageNo,
                  stationsData,
                  globalFilterApplied,
                  setGlobalFilterApplied,
                }}
              />
            ) : (
              <Tooltip title={t("permissionRequired")} followCursor>
                <Card
                  sx={{
                    opacity: 0.6,
                    height: "195px",
                    boxShadow: "none",
                    borderRadius: "8px",
                    "& .MuiCardContent-root": { padding: "16px !important" },
                  }}
                >
                  <CardContent>
                    <Box className={`flex gap-1 justify-between mb-1`}>
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#212B36",
                          lineHeight: 1.3,
                          mt: "4px",
                        }}
                      >
                        {t("targetVsSales")}
                      </Typography>
                    </Box>
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                  </CardContent>
                </Card>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={12} lg={6}>
            {checkPrivileges("ROLE_ANALYTICS_WORK_IN_PROGRESS") ? (
              <WorkInProgress
                user={loggedInUserData}
                themes={themeColors}
                stations={stations as { id: number; name: string }[] | []}
                handleDropdownActions={handleDropdownActions}
                globalFilters={watch("globalFilters")}
                {...{
                  enqueueSnackbar,
                  stationsLoaded,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  pageNo,
                  searchLoading,
                  setPageNo,
                  stationsData,
                  globalFilterApplied,
                  setGlobalFilterApplied,
                }}
              />
            ) : (
              <Tooltip title={t("permissionRequired")} followCursor>
                <Card
                  sx={{
                    opacity: 0.6,
                    height: "195px",
                    boxShadow: "none",
                    borderRadius: "8px",
                    "& .MuiCardContent-root": { padding: "16px !important" },
                  }}
                >
                  <CardContent>
                    <Box className={`flex gap-1 justify-between mb-1`}>
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#212B36",
                          lineHeight: 1.3,
                          mt: "4px",
                        }}
                      >
                        {t("workInProgress")}
                      </Typography>
                    </Box>
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                  </CardContent>
                </Card>
              </Tooltip>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            {checkPrivileges("ROLE_ANALYTICS_NET_SALES_TECHNICIAN") ? (
              <NetSalesTechnicians
                user={loggedInUserData}
                themes={themeColors}
                stations={stations as { id: number; name: string }[] | []}
                handleDropdownActions={handleDropdownActions}
                globalFilters={watch("globalFilters")}
                {...{
                  enqueueSnackbar,
                  stationsLoaded,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  pageNo,
                  searchLoading,
                  setPageNo,
                  stationsData,
                  globalFilterApplied,
                  setGlobalFilterApplied,
                }}
              />
            ) : (
              <Tooltip title={t("permissionRequired")} followCursor>
                <Card
                  sx={{
                    opacity: 0.6,
                    height: "270px",
                    marginTop: "16px",
                    boxShadow: "none",
                    borderRadius: "8px",
                    "& .MuiCardContent-root": { padding: "16px !important" },
                  }}
                >
                  <CardContent>
                    <Box className={`flex gap-1 justify-between mb-1`}>
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#212B36",
                          lineHeight: 1.3,
                          mt: "4px",
                        }}
                      >
                        {t("technicianSalesSummary")}
                      </Typography>
                    </Box>
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                  </CardContent>
                </Card>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={12} lg={6}>
            {checkPrivileges("ROLE_ANALYTICS_TOP_SALES_ITEMS") ||
            checkPrivileges("ROLE_ANALYTICS_TOP_SALES_SERVICES") ? (
              <TopSales
                user={loggedInUserData}
                themes={themeColors}
                stations={stations as { id: number; name: string }[] | []}
                handleDropdownActions={handleDropdownActions}
                globalFilters={watch("globalFilters")}
                {...{
                  enqueueSnackbar,
                  stationsLoaded,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  pageNo,
                  searchLoading,
                  setPageNo,
                  stationsData,
                  globalFilterApplied,
                  setGlobalFilterApplied,
                }}
              />
            ) : (
              <Tooltip title={t("permissionRequired")} followCursor>
                <Card
                  sx={{
                    opacity: 0.6,
                    height: "270px",
                    marginTop: "16px",
                    boxShadow: "none",
                    borderRadius: "8px",
                    "& .MuiCardContent-root": { padding: "16px !important" },
                  }}
                >
                  <CardContent>
                    <Box className={`flex gap-1 justify-between mb-1`}>
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#212B36",
                          lineHeight: 1.3,
                          mt: "4px",
                        }}
                      >
                        {t("topSales")}
                      </Typography>
                    </Box>
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                    <Skeleton
                      animation={false}
                      sx={{ backgroundColor: themeColors?.secondary }}
                    />
                  </CardContent>
                </Card>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_JOBCARD_OPEN_VS_CLOSED") ? (
          <JobCardOpenVsClosed
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "195px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-1`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("JobCardOpenVsClosed")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}

        {checkPrivileges("ROLE_ANALYTICS_CANCELLATION_SERVICE") ? (
          <ServiceCancellationReasons
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "270px",
                marginTop: "16px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-5`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("service") + " " + t("cancellationReasons")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_PAYMENT_DETAILS") ? (
          <PaymentsOverview
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "215px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-1`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("paymentStatusOverview")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  variant="circular"
                  width={125}
                  height={125}
                  sx={{
                    backgroundColor: themeColors?.secondary,
                    margin: "10px auto",
                  }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_BAY_COUNT") ? (
          <BayInformation
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "215px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-5`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("bayInfo")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  variant="circular"
                  width={95}
                  height={95}
                  sx={{
                    backgroundColor: themeColors?.secondary,
                    margin: "10px auto",
                  }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_CUSTOMER_STATS") ? (
          <CustomerBreakdown
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "215px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-5`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("CustomerBreakdown")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_TOP_SERVICE_PERFORMED") ? (
          <TopServicesPerformed
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-5`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("topServicesPerformed")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_SERVICE_BREAKDOWN_VEHICLE") ? (
          <TopMakeModelServices
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-5`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("makeModelTopServices")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{
                    backgroundColor: themeColors?.secondary,
                    marginTop: "8px",
                  }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} lg={4}>
        {checkPrivileges("ROLE_ANALYTICS_AVG_TIME_PER_JOBCARD") ? (
          <AverageTimePerJC
            user={loggedInUserData}
            themes={themeColors}
            stations={stations as { id: number; name: string }[] | []}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              stationsLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              pageNo,
              searchLoading,
              setPageNo,
              stationsData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className={`flex gap-1 justify-between mb-5`}>
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#212B36",
                      lineHeight: 1.3,
                      mt: "4px",
                    }}
                  >
                    {t("avgTimePerJC")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}
